import { Children, useMemo } from 'react';
// routes
import { paths } from 'src/routes/paths';
// locales
import { useLocales } from 'src/locales';
// components
import SvgColor from 'src/components/svg-color';
// auth
import { useAuthContext } from 'src/auth/hooks';
import path from 'path';

// ----------------------------------------------------------------------

const icon = (name: string, color: string = 'inherit') => (
  <SvgColor 
    src={`/assets/icons/navbar/${name}.svg`} 
    sx={{ 
      width: 1, 
      height: 1,
      color
    }} 
  />
);

const ICONS = {
  job: icon('ic_job', '#00A76F'),           // Green
  setting: icon('ic_settings', '#637381'),  // Gray
  blog: icon('ic_blog', '#8E33FF'),         // Purple
  chat: icon('ic_chat', '#00B8D9'),         // Cyan
  mail: icon('ic_mail', '#FF5630'),         // Red
  user: icon('ic_user', '#0C53B7'),         // Blue
  file: icon('ic_file', '#FFAB00'),         // Orange
  lock: icon('ic_lock', '#B71D18'),         // Dark Red
  tour: icon('ic_tour', '#22C55E'),         // Light Green
  order: icon('ic_order', '#FF6B93'),       // Pink
  label: icon('ic_label', '#006C9C'),       // Dark Blue
  blank: icon('ic_blank', '#919EAB'),       // Light Gray
  ques: icon('ic_ques', '#2065D1'),         // Royal Blue
  kanban: icon('ic_kanban', '#7635DC'),     // Dark Purple
  folder: icon('ic_folder', '#FFC107'),     // Amber
  banking: icon('ic_banking', '#078DEE'),   // Sky Blue
  booking: icon('ic_booking', '#00B8D9'),   // Cyan
  invoice: icon('ic_invoice', '#FF5630'),   // Red Orange
  product: icon('ic_product', '#00A76F'),   // Teal
  calendar: icon('ic_calendar', '#3366FF'),  // Blue
  disabled: icon('ic_disabled', '#919EAB'), // Gray
  external: icon('ic_external', '#FF5630'), // Red Orange
  menuItem: icon('ic_menu_item', '#637381'),// Medium Gray
  ecommerce: icon('ic_ecommerce', '#FF6F00'),// Deep Orange
  analytics: icon('ic_analytics', '#1890FF'),// Light Blue
  dashboard: icon('ic_dashboard', '#00B8D9'),// Cyan
  test: icon('ic_test', '#8E33FF'),         // Purple
  contractor: icon('ic_contractor', '#FF9800'),// Orange
};

// ----------------------------------------------------------------------

export function useNavData() {
  const { t } = useLocales();
  const { user } = useAuthContext();

  const data = useMemo(() => {
    const navigationData = [
      {
        subheader: t('Tổng quan'),
        items: (() => {
          if (!user) return [];
          return [
            {
              title: t('Phân tích'),
              path: paths.dashboard.root,
              icon: ICONS.analytics,
            },
          ];
        })(),
      },
      // OVERVIEW
      {
        subheader: t('Hệ thống'),
        items: (() => {
          if (!user) return [];
          if (user.isRole !== 4 && user.isRole !== 2) {
            return [
              {
                title: t('Danh mục cấu hình'),
                path: paths.dashboard.danhMucCauHinh.root,
                icon: ICONS.setting,
                children: [
                  { title: t('công ty'), path: paths.dashboard.danhMucCauHinh.congTy },
                  { title: t('văn bằng chứng chỉ'), path: paths.dashboard.danhMucCauHinh.vbccn },
                  // { title: t('nhà thầu'), path: paths.dashboard.danhMucCauHinh.nhaThau },
                  { title: t('tài liệu'), path: paths.dashboard.danhMucCauHinh.taiLieu },
                  { title: t('chức vụ và cấp bậc'), path: paths.dashboard.danhMucCauHinh.chucVu },
                  { title: t('bản đồ thăng tiến'), path: paths.dashboard.danhMucCauHinh.bandothangtien },
                  { title: t('thử thách'), path: paths.dashboard.danhMucCauHinh.thuthach },
                ],
              },
              {
                title: t('Quản lý nhân sự nhà thầu'),
                path: paths.dashboard.nhaThau.root,
                icon: ICONS.contractor,
                children: [
                  { title: t('Nhân sự'), path: paths.dashboard.nhaThau.root },
                  { title: t('Báo cáo'), path: paths.dashboard.nhaThau.analytics },
                  { title: t('Yêu cầu đã gửi'), path: paths.dashboard.nhaThau.yeucau },
                ],
              },
            ];
          }
          if (user.isRole === 4) {
            return [
              {
                title: t('Danh mục cấu hình'),
                path: paths.dashboard.danhMucCauHinh.root,
                icon: ICONS.setting,
                children: [
                  { title: t('chức vụ và cấp bậc'), path: paths.dashboard.danhMucCauHinh.chucVu },
                  { title: t('bản đồ thăng tiến'), path: paths.dashboard.danhMucCauHinh.bandothangtien },
                  { title: t('thử thách'), path: paths.dashboard.danhMucCauHinh.thuthach },
                ],
              },
              {
                title: t('Quản lý nhân sự nhà thầu'),
                path: paths.dashboard.nhaThau.root,
                icon: ICONS.contractor,
                children: [
                  { title: t('Nhân sự'), path: paths.dashboard.nhaThau.root },
                  { title: t('Báo cáo'), path: paths.dashboard.nhaThau.analytics },
                  { title: t('Yêu cầu đã gửi'), path: paths.dashboard.nhaThau.yeucau },
                ],
              },
            ];
          }
          return []; // ✅ thêm return mặc định
        })(),
      },
      {
        subheader: t('Đào tạo'),
        items: (() => {
          if (user?.isRole === 4 || user?.isRole === 2) {
            return [
              {
                title: t('Danh mục lớp học'),
                path: paths.dashboard.danhMucDaoTao.lopHoc,
                icon: ICONS.job,
              },
            ];
          }

          // Các role khác
          return [
            {
              title: t('Danh mục khóa học'),
              path: paths.dashboard.danhMucDaoTao.khoaHoc,
              icon: ICONS.file,
            },
            // {
            //   title: t('Danh mục giảng viên'),
            //   path: paths.dashboard.danhMucDaoTao.giangVien,
            //   icon: ICONS.user,
            // },
            {
              title: t('Danh mục câu hỏi'),
              path: paths.dashboard.danhMucDaoTao.nhch,
              icon: ICONS.ques,
            },
            {
              title: t('Danh mục lớp học'),
              path: paths.dashboard.danhMucDaoTao.lopHoc,
              icon: ICONS.job,
            },
            {
              title: t('Danh mục đăng ký'),
              path: paths.dashboard.danhMucDaoTao.dangKy,
              icon: ICONS.external,
            },
            {
              title: t('Danh mục bài thi'),
              path: paths.dashboard.danhMucDaoTao.baiThi,
              icon: ICONS.test,
            },
            {
              title: t('Danh mục chứng chỉ'),
              path: paths.dashboard.danhMucDaoTao.chungChi,
              icon: ICONS.blog,
            },
          ];
        })(),
      },
    ];

    return navigationData;
  }, [t, user]);

  return data;
}
